import { RefreshCcw, ChevronLeft, ChevronRight, Bell } from 'lucide-react';
import { Button } from '@ui/button';
import { Card, CardContent } from '@ui/card';
import { Skeleton } from '@ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ui/select';
import { cn } from '@/lib/utils';
import type { InboxStatusFilter } from '@/components/notifications/InboxSessionContext';

/**
 * Notifications header component
 */
export function NotificationsHeader({
  totalItems,
  unreadCount,
  isPending,
  isFetching,
  onRefresh,
  // onMarkAllAsRead,
  // markAsReadMutation
}: NotificationsHeaderProps) {
  return (
    <div className="mb-4 border-b border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Inbox</h2>
          <p className="text-gray-500">
            {totalItems} total {!!unreadCount && unreadCount > 0 && `• ${unreadCount} unread`}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button 
            onClick={onRefresh} 
            variant="outline" 
            disabled={isPending || isFetching} 
            className="bg-white border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-400 hover:shadow-md transition-all duration-200 cursor-pointer"
          >
            <RefreshCcw className={cn('mr-2 h-4 w-4', (isPending || isFetching) ? 'animate-spin' : '')} />
            Refresh
          </Button>
          {/* {unreadCount > 0 && (
            <Button 
              onClick={onMarkAllAsRead}
              variant="outline" 
              disabled={markAsReadMutation.isPending}
              className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 hover:border-blue-400 hover:shadow-md transition-all duration-200 cursor-pointer"
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              Mark All as Read
            </Button>
          )} */}
        </div>
      </div>
    </div>
  );
}

/**
 * Notifications pagination component
 */
export function NotificationsPagination({
  pageNo,
  pageSize,
  totalItems,
  totalPages,
  onPageChange,
  disablePagination,
  statusFilter,
  onStatusFilterChange,
}: NotificationsPaginationProps) {
  const startItem = (pageNo - 1) * pageSize + 1;
  const endItem = Math.min(pageNo * pageSize, totalItems);

  return (
    <div className="flex items-center justify-between border-b border-gray-200 px-1 pb-1.5">
      <div className="flex items-center gap-2">
        {statusFilter != null && onStatusFilterChange ? (
          <Select value={statusFilter} onValueChange={onStatusFilterChange}>
            <SelectTrigger
              size="sm"
              iconClassName="size-3"
              disabled={disablePagination}
              className="h-6 w-fit gap-1 rounded-full border-gray-200 bg-transparent px-2 text-xs font-medium text-gray-600 shadow-none hover:bg-gray-100 focus-visible:ring-0"
            >
              <SelectValue placeholder="Unread" />
            </SelectTrigger>
            <SelectContent align="start" className="min-w-[6.5rem]">
              <SelectItem value="unread" className="text-xs">
                Unread
              </SelectItem>
              <SelectItem value="read" className="text-xs">
                Read
              </SelectItem>
              <SelectItem value="all" className="text-xs">
                All
              </SelectItem>
            </SelectContent>
          </Select>
        ) : null}

        {totalItems > 0 && (
          <span className="text-sm text-gray-700">
            {startItem}-{endItem} of {totalItems}
          </span>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">
            {pageNo} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(pageNo - 1)}
            disabled={pageNo <= 1 || disablePagination}
            className="border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-400 hover:shadow-md transition-all duration-200 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(pageNo + 1)}
            disabled={pageNo >= totalPages || disablePagination}
            className="border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-400 hover:shadow-md transition-all duration-200 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Loading state component for notifications
 */
export function NotificationsLoadingState({ version }: NotificationsLoadingStateProps) {
  const skeletonCount = version === 'v1' ? 8 : 6;
  const spacingClass = version === 'v1' ? 'space-y-2' : 'space-y-3';
  const itemClass = version === 'v1' 
    ? 'flex items-start space-x-3 py-4 px-4 rounded-lg mx-2 border border-gray-200 bg-[var(--color-surface-light-sl-1)]'
    : 'flex items-start space-x-4 py-4 px-6 rounded-lg border border-gray-200 bg-[var(--color-surface-light-sl-1)]';

  return (
    <div className="flex h-full flex-col">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Notifications</h2>
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto">
        <div className={spacingClass}>
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <div key={index} className={itemClass}>
              {version === 'v1' ? (
                <Skeleton className="h-10 w-10 rounded-full" />
              ) : (
                <Skeleton className="h-2 w-2 rounded-full mt-1" />
              )}
              <div className="flex-1 space-y-2">
                <div className="flex items-start justify-between">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-16" />
                </div>
                <div className="flex items-center space-x-2">
                  <Skeleton className="h-5 w-16 rounded-full" />
                  <Skeleton className="h-5 w-20 rounded-full" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
export function NotificationsLoadingStateV3() {
  const skeletonCount = 6;

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto">
        {/* Notifications container skeleton */}
        <div>
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <div
              key={index}
              className="flex items-start gap-3 py-3 pr-4"
            >
              {/* Avatar skeleton - matches h-8 w-8 */}
              <Skeleton className="h-8 w-8 rounded-full shrink-0 bg-gray-200" />
              
              {/* Content skeleton */}
              <div className="flex-1 min-w-0">
                {/* Header: User name, "in", context, timestamp, orange dot */}
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-1.5 min-w-0 flex-1">
                    {/* User name skeleton */}
                    <Skeleton className="h-3 w-24 bg-gray-300" />
                    {/* "in" text - static */}
                    <span className="text-xs text-gray-600">in</span>
                    {/* Context skeleton */}
                    <Skeleton className="h-3 w-32 bg-gray-300" />
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {/* Timestamp skeleton */}
                    <Skeleton className="h-3 w-16 bg-orange-200" />
                    {/* Orange dot indicator */}
                    <div className="w-2 h-2 bg-orange-400 rounded-full shrink-0" />
                  </div>
                </div>

                {/* Message preview skeleton - matches text-xs line-clamp-2 */}
                <div className="space-y-1">
                  <Skeleton className="h-3 w-full bg-gray-200" />
                  <Skeleton className="h-3 w-3/4 bg-gray-200" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Error state component for notifications
 */
export function NotificationsErrorState({ onRetry }: NotificationsErrorStateProps) {
  return (
    <div className="flex h-full flex-col">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Inbox</h2>
          <p className="text-gray-500">Error loading history</p>
        </div>
      </div>
      
      <div className="flex-1 flex items-center justify-center">
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-red-500 mb-4">Failed to load history</p>
            <Button onClick={onRetry} variant="outline">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/**
 * Empty state component for notifications
 */
export function NotificationsEmptyState() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="text-center">
        <Bell className="text-gray-400 mx-auto mb-4 h-12 w-12" />
        <h3 className="mb-2 text-lg font-medium text-gray-900">No history found</h3>
        <p className="text-gray-500">You're all caught up. You can check back later.</p>
      </div>
    </div>
  );
}

type NotificationsHeaderProps = {
  /**
   * Total number of items
   */
  totalItems: number;
  /**
   * Number of unread notifications
   */
  unreadCount?: number;
  /**
   * Whether the notifications are pending
   */
  isPending: boolean;
  /**
   * Whether the notifications are fetching
   */
  isFetching: boolean;
  /**
   * Whether the mark all as read mutation is pending
   */
  // onMarkAllAsRead: () => void;
  /**
   * Mark all as read mutation
   */
  // markAsReadMutation: {
  // isPending: boolean;
  //  };
  /**
   * Function to refresh the notifications
   */
  onRefresh: () => void;
}

type NotificationsPaginationProps = {
  /**
   * Current page number
   */
  pageNo: number;
  /**
   * Page size
   */
  pageSize: number;
  /**
   * Total number of items
   */
  totalItems: number;
  /**
   * Total number of pages
   */
  totalPages: number;
  /**
   * Function to change the page
   */
  onPageChange: (page: number) => void;
  /**
   * Whether the pagination is disabled
   */
  disablePagination: boolean;
  /**
   * Current inbox status filter. Omit (with `onStatusFilterChange`) to render pagination
   * without the inbox-specific Unread/Read/All dropdown, e.g. when reused outside the inbox.
   */
  statusFilter?: InboxStatusFilter;
  /**
   * Function to change the status filter
   */
  onStatusFilterChange?: (statusFilter: InboxStatusFilter) => void;
}

type NotificationsLoadingStateProps = {
  /**
   * Version of the notifications
   */
  version: 'v1' | 'v2';
}

type NotificationsErrorStateProps = {
  /**
   * Function to retry the notifications
   */
  onRetry: () => void;
}
